import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Users, Calendar, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

function timeAgo(iso, now, lang) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(1, Math.floor((now - t) / 1000));
    if (lang === "it") {
      if (diff < 60) return `${diff} sec fa`;
      if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
      if (diff < 86400) {
        const h = Math.floor(diff / 3600);
        return h === 1 ? "1 ora fa" : `${h} ore fa`;
      }
      const d = Math.floor(diff / 86400);
      return d === 1 ? "1 giorno fa" : `${d} giorni fa`;
    }
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
}

// Twice-a-day deterministic slot — same 12h window shows the same selection
function currentSlot() {
  return Math.floor(Date.now() / (12 * 60 * 60 * 1000));
}

export default function LiveTicker() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [slot, setSlot] = useState(() => currentSlot());

  // Fetch on mount & on lang change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/recent-requests?lang=${lang}`);
        if (!cancelled) setItems(r.data || []);
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  // Tick every minute → updates the "X min/ore fa" labels live without changing the items
  useEffect(() => {
    const id = setInterval(() => {
      const next = Date.now();
      setNow(next);
      const s = Math.floor(next / (12 * 60 * 60 * 1000));
      // If the user kept the tab open across a slot boundary, advance the selection too
      setSlot((prev) => (prev !== s ? s : prev));
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const visible = useMemo(() => {
    if (items.length === 0) return [];
    const take = Math.min(3, items.length);
    if (items.length <= 3) return items.slice(0, take);
    const start = (slot * 3) % items.length;
    const out = [];
    for (let k = 0; k < take; k++) {
      out.push(items[(start + k) % items.length]);
    }
    return out;
  }, [items, slot]);

  if (visible.length === 0) return null;

  return (
    <div data-testid="live-ticker" className="mt-12 max-w-2xl">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-turquoise opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-turquoise" />
        </span>
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/80 font-semibold">
          {lang === "it" ? "Ultime richieste inoltrate" : "Latest requests submitted"}
        </p>
      </div>
      <div className="space-y-2">
        {visible.map((it, i) => (
          <div
            key={`ticker-${i}`}
            data-testid={`ticker-row-${i}`}
            className="rounded-xl bg-white/8 backdrop-blur border border-white/15 px-4 py-3 flex items-center justify-between gap-3 animate-fade-up"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div className="flex items-center gap-3 text-sm text-white min-w-0">
              <span className="inline-flex items-center gap-1 text-gold">
                <MapPin className="h-3.5 w-3.5" />
                <span className="font-medium">{it.destination}</span>
              </span>
              <span className="opacity-50">·</span>
              <span className="inline-flex items-center gap-1 opacity-90">
                <Users className="h-3.5 w-3.5" /> {it.num_participants || "?"}
                <span className="hidden sm:inline ml-0.5">{lang === "it" ? "persone" : "people"}</span>
              </span>
              {it.period ? (
                <>
                  <span className="opacity-50">·</span>
                  <span className="inline-flex items-center gap-1 opacity-90">
                    <Calendar className="h-3.5 w-3.5" /> {it.period}
                  </span>
                </>
              ) : null}
            </div>
            <span
              data-testid={`ticker-time-${i}`}
              className="text-xs text-white/60 whitespace-nowrap flex items-center gap-1"
            >
              <Sparkles className="h-3 w-3 text-gold" />
              {timeAgo(it.created_at, now, lang)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
